import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { Grid2DPreviewProps, CellValidation } from './types';

const StatusIcon = ({ status }: { status: 'valid' | 'warning' | 'error' }) => {
  switch (status) {
    case 'valid':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'warning':
      return <WarningIcon color="warning" fontSize="small" />;
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />;
  }
};

export const Grid2DPreview = ({
  config,
  validationResult,
  onValidate,
  loading = false,
}: Grid2DPreviewProps) => {
  const [selectedCell, setSelectedCell] = useState<CellValidation | null>(null);

  const { criteria } = config;

  const getCellValidation = (x: number, y: number): CellValidation | undefined => {
    return validationResult?.cells.find((c) => c.x === x && c.y === y);
  };

  const getCellColor = (status?: 'valid' | 'warning' | 'error'): string => {
    switch (status) {
      case 'valid':
        return '#e8f5e9';
      case 'warning':
        return '#fff3e0';
      case 'error':
        return '#ffebee';
      default:
        return '#f5f5f5';
    }
  };

  const handleCellClick = (cell: CellValidation | undefined) => {
    if (cell) {
      setSelectedCell(cell);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Grid Preview</Typography>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={onValidate}
            disabled={loading}
          >
            Validate All Cells
          </Button>
        </Box>

        {/* Validation Summary */}
        {validationResult && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${validationResult.validCells} valid`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<WarningIcon />}
              label={`${validationResult.warningCells} warning`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<ErrorIcon />}
              label={`${validationResult.errorCells} error`}
              color="error"
              variant="outlined"
            />
            {validationResult.valid ? (
              <Alert severity="success" sx={{ py: 0 }}>
                All cells have valid answers!
              </Alert>
            ) : (
              <Alert severity="error" sx={{ py: 0 }}>
                Some cells have no valid answers. Please adjust criteria.
              </Alert>
            )}
          </Box>
        )}

        {/* Grid Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>
                  X \ Y
                </TableCell>
                {criteria.y.map((yCriterion, yIndex) => (
                  <TableCell
                    key={yIndex}
                    align="center"
                    sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5' }}
                  >
                    {yCriterion.label || `Y${yIndex + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {criteria.x.map((xCriterion, xIndex) => (
                <TableRow key={xIndex}>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>
                    {xCriterion.label || `X${xIndex + 1}`}
                  </TableCell>
                  {criteria.y.map((_, yIndex) => {
                    const cell = getCellValidation(xIndex, yIndex);
                    return (
                      <TableCell
                        key={yIndex}
                        align="center"
                        sx={{
                          backgroundColor: getCellColor(cell?.status),
                          cursor: cell ? 'pointer' : 'default',
                          '&:hover': cell ? { opacity: 0.8 } : {},
                        }}
                        onClick={() => handleCellClick(cell)}
                      >
                        {cell ? (
                          <Tooltip title={`${cell.count} matching entities (Click to see)`}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                              }}
                            >
                              <StatusIcon status={cell.status} />
                              <Typography variant="body2">{cell.count}</Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            ?
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{ width: 16, height: 16, backgroundColor: '#e8f5e9', border: '1px solid #ccc' }}
            />
            <Typography variant="caption">3+ matches (valid)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{ width: 16, height: 16, backgroundColor: '#fff3e0', border: '1px solid #ccc' }}
            />
            <Typography variant="caption">1-2 matches (warning)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{ width: 16, height: 16, backgroundColor: '#ffebee', border: '1px solid #ccc' }}
            />
            <Typography variant="caption">0 matches (error)</Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Cell Details Dialog */}
      <Dialog open={!!selectedCell} onClose={() => setSelectedCell(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cell [{selectedCell?.x}, {selectedCell?.y}] - {selectedCell?.count} matches
        </DialogTitle>
        <DialogContent>
          {selectedCell?.entities && selectedCell.entities.length > 0 ? (
            <List dense>
              {selectedCell.entities.map((entity) => (
                <ListItem key={entity.id}>
                  <ListItemText
                    primary={entity.displayName}
                    secondary={entity.id.substring(0, 8) + '...'}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="textSecondary">
              No matching entities for this cell combination.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCell(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
